import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

// Create axios instance with defaults
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

// Track if we're refreshing the token to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

// Process the queue of failed requests
const processQueue = (error, token = null) => {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Add request interceptor for auth header
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle token refresh for 401 errors first
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue the request if already refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await api.post('/auth/refresh-token', { refreshToken });
        
        if (!response.data?.token) {
          throw new Error('Invalid refresh token response');
        }

        const { token } = response.data;
        localStorage.setItem('token', token);
        originalRequest.headers.Authorization = `Bearer ${token}`;
        
        // Process queued requests
        processQueue(null, token);
        
        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        // Handle refresh token failure
        processQueue(refreshError);
        
        // Clear auth data
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        
        return Promise.reject({
          success: false,
          code: 'AUTH_REFRESH_FAILED',
          status: 401,
          message: refreshError.response?.data?.message || 'Authentication failed'
        });
      } finally {
        isRefreshing = false;
        originalRequest._retry = false;
      }
    }

    // Handle all other non-2xx responses
    if (error.response?.status >= 400) {
      return Promise.reject({
        success: false,
        code: error.response.data.code || 'API_ERROR',
        status: error.response.status,
        message: error.response.data.message || 'An error occurred',
        data: error.response.data || null
      });
    }
    
    // Handle network errors
    if (error.request) {
      return Promise.reject({
        success: false,
        code: 'NETWORK_ERROR',
        message: 'Network error. Please check your connection.'
      });
    }
    
    // Handle request setup errors
    return Promise.reject({
      success: false,
      code: 'REQUEST_SETUP_ERROR',
      message: 'Error setting up the request'
    });
  }
);

export default api;